// 11 Sep 1906
//
// CENTRALIZED VITALS FORM LOGIC
// This custom hook contains ALL the complex vitals logic in one place:
// - Unit management with persistence across data changes
// - Field processing and memoization
// - Input handling with base unit conversions
// - Calculated field dependencies
// - Performance optimizations
//
// Used by: VitalsComponentInlineEntryComponent, VitalsComponentEntryDialog, VitalShowingComponent
//

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchVitalsFormSettings } from '../services/vitalsFormSettingsThunk'
import { selectVitalsFormSettingsData } from '../vitalsFormSettingsReducer'
import {
  processFieldsForDisplay,
  getDefaultUnit,
  convertToBaseUnit,
  convertFromBaseUnit,
  calculateFieldValue,
  hasVitalData,
  initializeVitalData,
} from '../utils/vitalsUtils'

export const useVitalsForm = ({
  existingVitalData = null,
  onDataChange = null,
  onDataChangeDebounceMs = 400,
  autoFetch = true,
  vitalsFormGetApiSignature,
  practitionerId, // Optional: required for support portal
}) => {
  const dispatch = useDispatch()
  const vitalsFormSettings = useSelector(selectVitalsFormSettingsData)

  const [vitalData, setVitalData] = useState({})
  const [selectedUnits, setSelectedUnits] = useState({})
  const [loading, setLoading] = useState(false)

  // Detect portal and determine default API signature if not provided
  const defaultApiSignature = useMemo(() => {
    if (vitalsFormGetApiSignature) {
      return vitalsFormGetApiSignature
    }
    
    // Detect portal based on URL path
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname
      
      if (pathname.startsWith('/ca-home/') || pathname.startsWith('/kiosk/') || pathname.startsWith('/ca/')) {
        // Clinic Assistant Portal
        // Note: This requires practitionerId and clinicLocationId, but we don't have them here
        // So we'll use a basic endpoint - components should pass the full signature
        return '/api/secure/clinic-assistant/vitals/form'
      }
      if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
        // Support Portal - use support portal API with practitioner-id
        if (practitionerId) {
          return `/api/secure/cc/support/practitioner/vitals/form?practitioner-id=${practitionerId}`
        }
        return '/api/secure/cc/support/practitioner/vitals/form'
      }
      if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
        // Mentor Portal (including live session routes)
        return '/api/secure/practitioner/vitals/form'
      }
    }
    
    // Default to practitioner API (mentor portal)
    return '/api/secure/practitioner/vitals/form'
  }, [vitalsFormGetApiSignature, practitionerId])

  // Memoized fields to prevent unnecessary re-renders and state resets
  const vitalsFields = useMemo(() => {
    return processFieldsForDisplay(vitalsFormSettings?.data || [])
  }, [vitalsFormSettings?.data])

  // Auto-fetch vitals form settings on mount
  useEffect(() => {
    if (autoFetch && defaultApiSignature) {
      // Only fetch if data is not already loaded or if state is not loading
      const currentState = vitalsFormSettings?.state
      const hasData = vitalsFormSettings?.data && vitalsFormSettings.data.length > 0
      
      // Only fetch if we don't have data or if we're not already loading
      if (!hasData && currentState !== 'loading') {
        dispatch(fetchVitalsFormSettings({ 
          apiSignature: defaultApiSignature,
          practitionerId,
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, autoFetch, defaultApiSignature, practitionerId])

  // Initialize units ONLY when fields first load (preserve user selections)
  useEffect(() => {
    if (vitalsFields.length > 0) {
      setSelectedUnits((prevUnits) => {
        const initialUnits = {}
        vitalsFields.forEach((field) => {
          const defaultUnit = getDefaultUnit(field)
          if (defaultUnit) {
            // Keep existing user selection, or use default for new fields
            initialUnits[field.id] = prevUnits[field.id] || defaultUnit
          }
        })
        return initialUnits
      })
    }
  }, [vitalsFields]) // Only depend on vitalsFields, NOT existingVitalData

  // Update vital data when existingVitalData changes (without resetting units)
  useEffect(() => {
    if (vitalsFields.length > 0) {
      const initialData = initializeVitalData(vitalsFields)
      setVitalData(existingVitalData || initialData)
    }
  }, [vitalsFields, existingVitalData])

  // Debounce timer ref for onDataChange notifications — avoids context updates on every keystroke
  const _onDataChangeTimer = useRef(null)
  const _onDataChangeRef = useRef(onDataChange)
  _onDataChangeRef.current = onDataChange

  useEffect(() => () => clearTimeout(_onDataChangeTimer.current), [])

  // Handle input changes with unit conversions and calculated field updates
  const handleInputChange = useCallback(
    (fieldId, inputValue, selectedUnit) => {
      const field = vitalsFields.find((f) => f.id === fieldId)
      if (!field) return

      let processedValue = null

      if (inputValue && !isNaN(inputValue)) {
        processedValue = parseFloat(inputValue)

        // Convert to base unit for storage
        if (selectedUnit) {
          processedValue = convertToBaseUnit(processedValue, field, selectedUnit)
        }
      }

      const newVitalData = {
        ...vitalData,
        [fieldId]: processedValue,
      }

      // Only recalculate fields that depend on the changed field (performance optimization)
      vitalsFields.forEach((vitalField) => {
        if (vitalField.type === 'calculated' && vitalField.compute?.dependsOn) {
          const dependsOnChangedField = vitalField.compute.dependsOn.some(
            (dep) => dep.code === fieldId,
          )
          if (dependsOnChangedField) {
            const calculatedValue = calculateFieldValue(vitalField, newVitalData)
            newVitalData[vitalField.id] = calculatedValue
          }
        }
      })

      setVitalData(newVitalData)

      // Debounce parent context update to avoid re-rendering all consumers on every keystroke
      if (_onDataChangeRef.current) {
        clearTimeout(_onDataChangeTimer.current)
        _onDataChangeTimer.current = setTimeout(() => {
          _onDataChangeRef.current(newVitalData)
        }, onDataChangeDebounceMs)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vitalsFields, vitalData, onDataChangeDebounceMs],
  )

  // Handle unit changes (preserve base values, only change display)
  const handleUnitChange = useCallback((fieldId, newUnit) => {
    setSelectedUnits((prev) => ({ ...prev, [fieldId]: newUnit }))
    // Don't re-trigger input change - the stored value in base units stays the same
    // Only the display conversion changes, which happens automatically in render
  }, [])

  // Get display value for a field (converted from base unit)
  const getDisplayValue = useCallback(
    (field) => {
      const selectedUnit = selectedUnits[field.id] || getDefaultUnit(field)
      const rawValue = vitalData[field.id]

      if (rawValue !== null && selectedUnit) {
        const converted = convertFromBaseUnit(rawValue, field, selectedUnit)
        // Strip floating-point noise from unit conversions (e.g. 37°C → "96.799999°F" becomes 96.8)
        // for DISPLAY only — stored base values are untouched. 2 decimals keeps real vitals precision
        // (directly-entered values already have ≤2 decimals) while removing the long-tail artifacts.
        return typeof converted === 'number' && Number.isFinite(converted)
          ? Math.round(converted * 100) / 100
          : converted
      }
      return rawValue
    },
    [vitalData, selectedUnits],
  )

  // Check if any vital data exists
  const hasData = useMemo(() => {
    return hasVitalData(vitalData, vitalsFields)
  }, [vitalData, vitalsFields])

  return {
    // Data
    vitalsFields,
    vitalData,
    selectedUnits,
    loading,
    hasData,

    // Actions
    handleInputChange,
    handleUnitChange,
    setLoading,

    // Utilities
    getDisplayValue,
    getDefaultUnit: (field) => getDefaultUnit(field),

    // For external use
    setVitalData,
    setSelectedUnits,
  }
}
